import React, { useCallback, useMemo } from 'react';
import { Star, Users, TrendingUp, Award, Globe2, Zap, ShieldCheck, Smartphone } from 'lucide-react';
import { motion, useReducedMotion, Transition } from 'framer-motion';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import type { LazyLoadImageProps } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';

interface StoreRating {
  playStore?: {
    rating: number;
    reviews: number;
    url: string;
  };
  appStore?: {
    rating: number;
    reviews: number;
    url: string;
  };
}

interface MultiPlatformHighlightProps {
  title: string;
  description: string;
  imageUrl: string;
  mobileImageUrl: string;
  storeRatings: StoreRating;
  stats: {
    users?: number;
    pageViews?: number;
    downloads?: number;
    achievement?: string;
  };
  metrics: {
    webPerformance?: number;
    mobilePerformance?: number;
    crossPlatformSync?: number;
  };
  interestingFact: string;
}

const MultiPlatformHighlight: React.FC<MultiPlatformHighlightProps> = ({
  title,
  description,
  imageUrl,
  mobileImageUrl,
  storeRatings,
  stats,
  metrics,
  interestingFact
}) => {
  // Check if user prefers reduced motion
  const prefersReducedMotion = useReducedMotion();

  // Memoize animation variants
  const contentAnimation = useMemo(() => ({
    hidden: { opacity: 0, x: prefersReducedMotion ? 0 : -20 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.4 }
    }
  }), [prefersReducedMotion]);

  const imageAnimation = useMemo(() => ({
    hidden: { opacity: 0, x: prefersReducedMotion ? 0 : 20 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.4 }
    }
  }), [prefersReducedMotion]);

  // Memoize hover animations
  const hoverAnimation = useMemo(() => ({
    scale: prefersReducedMotion ? 1 : 1.02,
    boxShadow: "0 4px 12px rgba(0,0,0,0.05)"
  }), [prefersReducedMotion]);

  // Memoize mobile device animation
  const mobileDeviceAnimation = useMemo(() => ({
    animate: prefersReducedMotion ? {} : {
      rotate: [1, -1, 1],
      y: [0, 3, 0]
    },
    transition: {
      duration: 7,
      repeat: Infinity,
      repeatType: "mirror" as const,
      ease: "easeInOut"
    }
  }), [prefersReducedMotion]);

  // Memoize decorative animations
  const decorativeAnimation = useMemo(() => ({
    animate: prefersReducedMotion ? {} : {
      scale: [1, 1.1, 1],
      opacity: [0.5, 0.7, 0.5]
    },
    transition: {
      duration: 4,
      repeat: Infinity,
      repeatType: "mirror" as const
    } as Transition
  }), [prefersReducedMotion]);

  // Memoize stat formatting
  const formatNumber = useCallback((num: number) => {
    return num.toLocaleString();
  }, []);

  return (
    <section className="py-24 bg-gradient-to-br from-slate-50 to-white relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(142,227,205,0.1)_0%,rgba(255,255,255,0)_60%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(155,135,245,0.1)_0%,rgba(255,255,255,0)_60%)]" />

      <div className="container px-4 mx-auto relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <motion.div
              variants={contentAnimation}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              className="relative z-20"
            >
              <h2 className="text-3xl font-bold text-slate-900 mb-6">{title}</h2>
              <p className="text-lg text-slate-600 mb-8">{description}</p>

              {/* Store ratings */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                {storeRatings.playStore && (
                  <motion.a 
                    href={storeRatings.playStore.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex gap-2 items-center p-4 bg-white rounded-xl border border-slate-200 hover:border-orcsys-200 transition-colors"
                    whileHover={hoverAnimation}
                    whileTap={{ scale: prefersReducedMotion ? 1 : 0.98 }}
                  >
                    <LazyLoadImage
                      src="/google-play-color.svg"
                      alt="Google Play"
                      className="w-8 h-8 mr-3"
                      effect="blur"
                      width={32}
                      height={32}
                    />
                    <div>
                      <div className="flex items-center mb-1">
                        <Star className="w-4 h-4 text-yellow-400 mr-1" />
                        <span className="font-semibold">{storeRatings.playStore.rating}</span>
                      </div>
                      <div className="text-sm text-slate-500">
                        {formatNumber(storeRatings.playStore.reviews)} avis
                      </div>
                    </div>
                  </motion.a>
                )}

                {storeRatings.appStore && (
                  <motion.a 
                    href={storeRatings.appStore.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex gap-2 items-center p-4 bg-white rounded-xl border border-slate-200 hover:border-orcsys-200 transition-colors"
                    whileHover={hoverAnimation}
                    whileTap={{ scale: prefersReducedMotion ? 1 : 0.98 }}
                  >
                    <LazyLoadImage
                      src="/app-store-color.svg"
                      alt="App Store"
                      className="w-8 h-8 mr-3"
                      effect="blur"
                      width={32}
                      height={32}
                    />
                    <div>
                      <div className="flex items-center mb-1">
                        <Star className="w-4 h-4 text-yellow-400 mr-1" />
                        <span className="font-semibold">{storeRatings.appStore.rating}</span>
                      </div>
                      <div className="text-sm text-slate-500">
                        {formatNumber(storeRatings.appStore.reviews)} avis
                      </div>
                    </div>
                  </motion.a>
                )}
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                {Object.entries({
                  users: { icon: Users, label: "Utilisateurs actifs" },
                  pageViews: { icon: Globe2, label: "Pages vues / mois" },
                  downloads: { icon: TrendingUp, label: "Téléchargements" },
                  achievement: { icon: Award, label: "Distinction" }
                }).map(([key, { icon: Icon, label }]) => {
                  const value = stats[key as keyof typeof stats];
                  if (!value) return null;

                  return (
                    <motion.div 
                      key={key}
                      className="bg-white p-4 rounded-xl border border-slate-200"
                      whileHover={hoverAnimation}
                      transition={{ type: "spring", stiffness: 400, damping: 20 }}
                    >
                      <Icon className="w-6 h-6 text-orcsys-500 mb-2" />
                      <div className="text-2xl font-bold text-slate-900 mb-1">
                        {typeof value === 'number' ? `${formatNumber(value)}+` : value}
                      </div>
                      <div className="text-sm text-slate-600">{label}</div>
                    </motion.div>
                  );
                })}
              </div>

              {/* Performance Metrics */}
              <motion.div 
                className="bg-white p-6 rounded-xl border border-slate-200 mb-8"
                whileHover={hoverAnimation}
                transition={{ type: "spring", stiffness: 400, damping: 20 }}
              >
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Performance Multi-Plateforme</h3>
                <div className="grid grid-cols-3 gap-4">
                  {Object.entries({
                    webPerformance: { icon: Globe2, label: "Web" },
                    mobilePerformance: { icon: Smartphone, label: "Mobile" },
                    crossPlatformSync: { icon: Zap, label: "Sync" }
                  }).map(([key, { icon: Icon, label }], index) => {
                    const value = metrics[key as keyof typeof metrics];
                    if (!value) return null;

                    return (
                      <motion.div
                        key={key}
                        initial={{ scale: 0.9, opacity: 0 }}
                        whileInView={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.4, delay: index * 0.2 }}
                        viewport={{ once: true, margin: "-50px" }}
                      >
                        <div className="flex items-center justify-center w-16 h-16 rounded-full bg-orcsys-50 mx-auto mb-2">
                          <Icon className="w-8 h-8 text-orcsys-500" />
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-slate-900">
                            {value}%
                          </div>
                          <div className="text-sm text-slate-600">{label}</div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>

              {/* Interesting fact */}
              <motion.div 
                className="bg-orcsys-50 p-6 rounded-xl border border-orcsys-100"
                whileHover={hoverAnimation}
                transition={{ type: "spring", stiffness: 400, damping: 20 }}
              >
                <div className="text-sm font-medium text-orcsys-600 mb-2">Le saviez-vous ?</div>
                <p className="text-slate-700">{interestingFact}</p>
              </motion.div>
            </motion.div>

            {/* Images */}
            <motion.div
              variants={imageAnimation}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              className="relative z-10"
            >
              {/* Web Screenshot */}
              <motion.div 
                className="relative rounded-2xl overflow-hidden shadow-2xl mb-6"
                whileHover={hoverAnimation}
                transition={{ type: "spring", stiffness: 400, damping: 20 }}
              >
                <LazyLoadImage
                  src={imageUrl}
                  alt={`${title} - Version Web`}
                  className="w-full h-auto"
                  effect="blur"
                  threshold={100}
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-orcsys-500/10 via-transparent to-orcsys-500/10" />
              </motion.div>

              {/* Mobile Screenshot */}
              <div className="relative w-1/3 ml-auto -mt-32">
                <motion.div
                  {...mobileDeviceAnimation}
                  whileHover={{ scale: prefersReducedMotion ? 1 : 1.05 }}
                  style={{ transformOrigin: "center center" }}
                >
                  <div className="relative border-[10px] border-slate-800 rounded-[40px] shadow-2xl overflow-hidden 
                    h-[470px] w-[230px]"
                  >
                    <LazyLoadImage
                      src={mobileImageUrl}
                      alt={`${title} - Version Mobile`}
                      className="w-full h-full object-fill"
                      effect="blur"
                      threshold={100}
                    />
                    <div className="absolute inset-0 bg-gradient-to-tr from-orcsys-500/10 via-transparent to-orcsys-500/10" />
                  </div>
                </motion.div>
              </div>
              
              {/* Decorative elements */}
              {!prefersReducedMotion && (
                <>
                  <motion.div 
                    className="absolute -top-6 -right-6 w-24 h-24 bg-orcsys-500/10 rounded-full blur-2xl"
                    {...decorativeAnimation}
                  />
                  <motion.div 
                    className="absolute -bottom-6 -left-6 w-24 h-24 bg-orcsys-500/10 rounded-full blur-2xl"
                    {...decorativeAnimation}
                    transition={{
                      ...decorativeAnimation.transition,
                      delay: 2
                    }}
                  />
                </>
              )}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-orcsys-300/10 rounded-full blur-3xl -z-10" />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default React.memo(MultiPlatformHighlight); 